import { Feature, Map, View, Geolocation, Overlay } from 'ol';
import PopupFeature from 'ol-ext/overlay/PopupFeature';
import { FullScreen } from 'ol/control';
import { pointerMove, singleClick } from 'ol/events/condition';
import { Point } from 'ol/geom';
import { Draw, Modify } from 'ol/interaction';
import Select from 'ol/interaction/Select';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import React, { createContext, useContext, useState } from 'react';
import { MAP_EVENTS } from './constants';
import { createVectorSourceFromObjects, toPolygon } from './utils';
const MapContext = /*#__PURE__*/createContext();
const defaultZoomOptions = {
  duration: 1500,
  padding: [50, 50, 50, 50]
};
const formatArea = area => {
  return `${(area / 10000).toFixed(2)} ha`;
};
class MapInternalClass extends EventTarget {
  constructor(options) {
    super();
    const newView = new View(options.viewOptions);
    const controls = [new FullScreen()];
    this.map = new Map({
      layers: [],
      controls: options.controls ?? controls,
      overlays: [],
      view: newView
    });
    this.layerVisibility = {};
    this.map.on('moveend', this.internalMoveend.bind(this));

    // register select
    if (options.selectOptions !== null) {
      this.selected = null;
      this.selectSingleClick = new Select(options.selectOptions);
      this.selectSingleClick.on('select', e => this.internalSelect(e));
      this.map.addInteraction(this.selectSingleClick);
    }

    // drawing
    this.drawSelect = null;
    this.drawHistory = null;
    this.drawLayerSource = new VectorSource({
      wrapX: false
    });
    this.drawOptions = options.drawOptions;
    this.drawLayer = new VectorLayer({
      source: this.drawLayerSource,
      zIndex: 100,
      visible: false
    });
    this.map.addLayer(this.drawLayer);
    this.drawInteraction = null;
    this.pointsArrayStartLength = 0;
    this.pointsArray = [];
    this.modify = null;
    this.selectLayerSelect = null;
    this.selectLayerSource = new VectorSource({
      wrapX: false
    });
    this.selectLayerOptions = options.selectLayerOptions;
    this.selectLayer = new VectorLayer({
      source: this.selectLayerSource,
      zIndex: 101,
      visible: false
    });
    this.map.addLayer(this.selectLayer);
    this.drawGeometry = null;
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'tooltip tooltip-measure';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center'
    });

    // popup
    this.popupTemplate = options.popupTemplate;
    this.popupFeature = null;
    this.popupCondition = options.popupCondition;
    if (process.env.NODE_ENV === 'development') {
      window.map = this.map;
    }

    // set up geolocation only if the property is not null
    if (options.geolocationOptions !== null) {
      this.geoLocationStyle = options.geolocationOptions.geoLocationStyle;
      this.geolocation = new Geolocation({
        // enableHighAccuracy must be set to true to have the heading value.
        trackingOptions: {
          enableHighAccuracy: true
        },
        projection: this.map.getView().getProjection()
      });
      const accuracyFeature = new Feature();
      this.geolocation.on('change:accuracyGeometry', () => {
        accuracyFeature.setGeometry(this.geolocation.getAccuracyGeometry());
      });
      const positionFeature = new Feature();
      positionFeature.setStyle(new Style({
        ...this.geoLocationStyle
      }));
      this.geolocation.on('change:position', () => {
        const coordinates = this.geolocation.getPosition();
        positionFeature.setGeometry(coordinates ? new Point(coordinates) : null);
      });
      this.positionLayer = new VectorLayer({
        source: new VectorSource({
          features: [accuracyFeature, positionFeature]
        }),
        zIndex: 1000,
        properties: {
          name: 'GEOLOCATION'
        }
      });
      this.geolocation.setTracking(false);
      this.positionLayer.setVisible(false);
      this.map.addLayer(this.positionLayer);
    }
  }
  internalSelect(event) {
    // select the clicked element, null if nothing is selected
    const singleElement = event.selected.length === 0 ? null : event.selected[0];
    this.selected = null;
    if (singleElement) {
      const props = singleElement.getGeometry().getProperties();
      this.selected = {
        ...props
      };
    }
    // trigger selection event
    const e = new Event(MAP_EVENTS.selection);
    e.selected = this.selected === null ? null : {
      ...this.selected
    };
    this.dispatchEvent(e);
  }
  internalMoveend() {
    const e = new Event(MAP_EVENTS.extentChange);
    const extent = this.map.getView().calculateExtent();
    e.extent = extent;
    this.dispatchEvent(e);
  }
  getSelected() {
    return this.selected === null ? null : {
      ...this.selected
    };
  }
  setSelected(item, layerName = '') {
    const deselected = this.selectSingleClick.getFeatures().getArray();
    if (item === null) {
      this.selectSingleClick.dispatchEvent({
        type: 'select',
        selected: [],
        deselected
      });
      return;
    }
    if (layerName === '') {
      throw new Error(`No layer is defined`);
    }
    const itemCode = item.code;
    const selectedLayer = this.map.getAllLayers().find(e => e.get('name') === layerName);
    const selectedFeature = selectedLayer.getSource().getFeatures().find(e => e.getGeometry().getProperties().code === itemCode);
    const selectedFeatures = this.selectSingleClick.getFeatures();
    selectedFeatures.clear();
    selectedFeatures.push(selectedFeature);
    this.selectSingleClick.dispatchEvent({
      type: 'select',
      selected: selectedFeatures.getArray(),
      deselected
    });
  }
  getExtent() {
    return this.map.getView().calculateExtent();
  }
  setLayerVisibility(layerName, newVisibility) {
    if (typeof this.layerVisibility[layerName] === 'undefined') {
      throw new Error(`No layer: ${layerName}`);
    }
    this.layerVisibility[layerName] = newVisibility;
    const e = new Event(MAP_EVENTS.visibilityChange);
    e.layerVisibility = {
      ...this.layerVisibility
    };
    this.dispatchEvent(e);
  }
  getLayerVisibility(layerName) {
    if (typeof layerName === 'undefined') {
      return {
        ...this.layerVisibility
      };
    }
    if (typeof this.layerVisibility[layerName] === 'undefined') {
      throw new Error(`No layer: ${layerName}`);
    }
    return this.layerVisibility[layerName];
  }
  setDomElement(domElement) {
    this.map.setTarget(domElement);
  }
  addLayer(layerName, olLayer, defaultVisibility = true) {
    olLayer.setVisible(defaultVisibility);
    this.map.addLayer(olLayer);
    this.layerVisibility[layerName] = defaultVisibility;
    const e = new Event(MAP_EVENTS.visibilityChange);
    e.layerVisibility = {
      ...this.layerVisibility
    };
    this.dispatchEvent(e);
  }
  removeLayer(layer) {
    this.map.removeLayer(layer);
  }
  setLayerData(layerName, layerData = {}) {
    if (typeof this.layerVisibility[layerName] === 'undefined') {
      throw new Error(`No layer: ${layerName}`);
    }
    const e = new Event(MAP_EVENTS.layerData);
    e.layerData = layerData;
    e.layerName = layerName;
    this.dispatchEvent(e);
  }
  zoomToGeomsExtent(arrayOfGeoms, options) {
    const view = this.map.getView();
    const vs = createVectorSourceFromObjects(arrayOfGeoms);
    view.fit(vs.getExtent(), {
      ...defaultZoomOptions,
      ...options
    });
  }
  getZoom() {
    return this.map.getView().getZoom();
  }
  drawPolygon() {
    // remove previous interaction if present
    if (this.drawInteraction !== null) {
      this.map.removeInteraction(this.drawInteraction);
    }
    if (this.modify !== null) {
      this.map.removeInteraction(this.modify);
    }
    this.drawLayer.setVisible(true);
    this.drawInteraction = new Draw({
      source: this.drawLayerSource,
      stopClick: true,
      type: 'Polygon'
    });
    this.map.addOverlay(this.measureTooltip);
    // Set feature on drawstart
    this.drawInteraction.on('drawstart', event => {
      this.drawGeometry = event.feature;
      this.drawGeometry.getGeometry().on('change', ev => {
        const geom = ev.target;
        this.measureTooltip.setPosition(geom.getInteriorPoint().getCoordinates().slice(0, 2));
        this.measureTooltipElement.innerHTML = formatArea(geom.getArea());
      });
    });
    this.drawInteraction.on('drawend', event => {
      const geom = event.feature.getGeometry();
      this.measureTooltip.setPosition(geom.getInteriorPoint().getCoordinates().slice(0, 2));
      this.measureTooltipElement.innerHTML = formatArea(geom.getArea());
      if (this.drawInteraction !== null) {
        this.map.removeInteraction(this.drawInteraction);
        this.drawInteraction = null;
      }
    });
    this.modify = new Modify({
      source: this.drawLayerSource
    });
    this.map.addInteraction(this.drawInteraction);
    this.map.addInteraction(this.modify);
    this.modify.on('modifystart', event => {
      const [feature] = event.features.getArray();
      this.drawGeometry = feature;
      this.drawGeometry.getGeometry().on('change', ev => {
        const geom = ev.target;
        this.measureTooltip.setPosition(geom.getInteriorPoint().getCoordinates().slice(0, 2));
        this.measureTooltipElement.innerHTML = formatArea(geom.getArea());
      });
    });
  }
  drawPoints(geoms) {
    if (!geoms) {
      return;
    }
    if (geoms) {
      this.pointsArrayStartLength = geoms.length;
      this.pointsArray = geoms.map(e => new Feature(toPolygon(e)));
      this.drawLayerSource.addFeatures(this.pointsArray);
    }

    // remove previous interactions if present
    if (this.drawInteraction !== null) {
      this.map.removeInteraction(this.drawInteraction);
    }
    if (this.modify !== null) {
      this.map.removeInteraction(this.modify);
    }
    this.modify = new Modify({
      source: this.drawLayerSource
    });
    this.drawLayer.setVisible(true);
    this.drawLayer.setStyle(feature => this.drawOptions.drawLayerStyle(feature));
    this.drawInteraction = new Draw({
      source: this.drawLayerSource,
      // stopClick: true,
      type: 'Point',
      style: new Style({
        ...this.drawOptions.drawStyle
      })
    });
    this.drawInteraction.on('drawend', event => {
      this.pointsArray.push(event.feature);
    });
    this.map.addInteraction(this.modify);
    this.map.addInteraction(this.drawInteraction);
  }
  drawEdit(feature) {
    // remove previous interaction if present
    if (this.drawInteraction !== null) {
      this.map.removeInteraction(this.drawInteraction);
    }
    if (this.modify !== null) {
      this.map.removeInteraction(this.modify);
    }
    this.map.addOverlay(this.measureTooltip);
    const fGeom = feature.getGeometry();
    let fCoords;
    if (fGeom.getType() === 'MultiPolygon') {
      [fCoords] = fGeom.getInteriorPoints().getCoordinates();
    } else {
      fCoords = fGeom.getInteriorPoint().getCoordinates();
    }
    this.measureTooltip.setPosition(fCoords.slice(0, 2));
    this.measureTooltipElement.innerHTML = formatArea(fGeom.getArea());
    this.drawLayer.setVisible(true);
    this.drawLayerSource.addFeature(feature);
    this.drawInteraction = new Draw({
      source: this.drawLayerSource,
      stopClick: true,
      type: 'Polygon'
    });
    this.drawInteraction.on('drawend', event => {
      const geom = event.feature.getGeometry();
      this.measureTooltip.setPosition(geom.getInteriorPoint().getCoordinates().slice(0, 2));
      this.measureTooltipElement.innerHTML = formatArea(geom.getArea());
      if (this.drawInteraction !== null) {
        this.map.removeInteraction(this.drawInteraction);
        this.drawInteraction = null;
      }
    });
    this.modify = new Modify({
      source: this.drawLayerSource
    });
    this.map.addInteraction(this.drawInteraction);
    this.map.addInteraction(this.modify);
    feature.getGeometry().on('change', ev => {
      const geom = ev.target;
      let coords;
      if (geom.getType() === 'MultiPolygon') {
        [coords] = geom.getInteriorPoints().getCoordinates();
      } else {
        coords = geom.getInteriorPoint().getCoordinates();
      }
      this.measureTooltip.setPosition(coords.slice(0, 2));
      this.measureTooltipElement.innerHTML = formatArea(geom.getArea());
    });
  }
  drawPolygonUndo() {
    if (this.drawInteraction !== null) {
      this.drawInteraction.removeLastPoint();
    }
  }
  internalPointDeleteUndo() {
    if (this.drawSelect === null || this.drawHistory === null) {
      return;
    }
    const feature = this.drawHistory;
    const deletedSequenceId = feature.getGeometry().getProperties().sequenceId;
    const idx = this.pointsArray.findIndex(e => e.getGeometry().getProperties().sequenceId === deletedSequenceId);
    if (idx !== -1) {
      this.pointsArray.splice(idx, 1);
    }
    this.drawLayerSource.addFeature(feature);
    this.drawHistory = null;
  }
  internalPointAddUndo() {
    if (this.drawInteraction === null) {
      return;
    }
    const numberOfPoints = this.pointsArray.length;
    if (numberOfPoints === 0) {
      return;
    }
    if (numberOfPoints > this.pointsArrayStartLength) {
      this.pointsArray = [...this.pointsArray.slice(0, numberOfPoints - 1)];
      this.drawLayerSource.clear();
      this.drawLayerSource.addFeatures(this.pointsArray);
    }
  }

  // type stands for the action during which we pressed undo
  drawPointUndo(actionType) {
    switch (actionType) {
      case 'delete':
        this.internalPointDeleteUndo();
        break;
      case 'add':
        this.internalPointAddUndo();
        break;
      default:
        break;
    }
  }
  drawDelete(featureGeoms) {
    const features = featureGeoms.map(e => new Feature(toPolygon(e)));
    this.drawLayerSource.addFeatures(features);
    this.pointsArray = features;
    this.drawSelect = new Select({
      style: new Style({
        ...this.drawOptions.drawStyle
      }),
      layers: [this.drawLayer]
    });
    this.drawSelect.on('select', event => {
      const selected = event.selected.length > 0 ? event.selected[0] : null;
      if (!selected) {
        return;
      }
      this.drawHistory = selected;
      const {
        sequenceId
      } = selected.getGeometry().getProperties();
      const filteredFeatures = this.pointsArray.filter(e => e.getGeometry().getProperties().sequenceId !== sequenceId);
      this.pointsArray = filteredFeatures;
      this.drawLayerSource.clear();
      this.drawLayerSource.addFeatures(filteredFeatures);
    });
    this.drawLayer.setVisible(true);
    this.map.removeInteraction(this.drawInteraction);
    this.map.removeInteraction(this.modify);
    this.map.addInteraction(this.drawSelect);
    this.map.removeOverlay(this.measureTooltip);
  }
  drawClear() {
    this.drawLayerSource.clear();
    this.pointsArray = [];
    this.drawHistory = [];
  }
  drawCancel() {
    // if the 'drawend' event did not fired yet, we have to cancel the drawing interaction
    if (this.drawInteraction !== null) {
      this.drawInteraction.abortDrawing();
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
    if (this.drawSelect !== null) {
      this.map.removeInteraction(this.drawSelect);
      this.drawSelect = null;
    }
    this.map.removeOverlay(this.measureTooltip);
    this.measureTooltip.setPosition(undefined);
    this.drawLayer.setVisible(false);
    this.drawLayerSource.clear();
    this.pointsArray = [];
    this.drawHistory = [];
  }
  drawSave() {
    if (this.drawInteraction !== null) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
    this.map.removeOverlay(this.measureTooltip);
    this.drawLayer.setVisible(false);
    const features = this.drawLayerSource.getFeatures();
    this.drawLayerSource.clear();
    this.pointsArray = [];
    this.drawHistory = [];
    return features;
  }
  drawPointsSave() {
    if (this.drawInteraction !== null) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
    if (this.modify !== null) {
      this.map.removeInteraction(this.modify);
      this.modify = null;
    }
    this.drawLayer.setVisible(false);
    const features = this.pointsArray;
    this.drawLayerSource.clear();
    this.pointsArray = [];
    this.drawHistory = [];
    return features;
  }
  internalSelect2(event) {
    // select the clicked element, null if nothing is selected
    const singleElement = event.selected.length === 0 ? null : event.selected[0];
    this.selected = null;
    if (singleElement) {
      const props = singleElement.getGeometry().getProperties();
      // TODO: this can be added to internalSelect (maybe)
      this.selected = {
        ...props,
        geom: singleElement.getGeometry()
      };
    }
    // trigger selection event
    const e = new Event(MAP_EVENTS.selection);
    e.selected = this.selected === null ? null : {
      ...this.selected
    };
    this.dispatchEvent(e);
  }
  selectLayerStartSelect(geoms) {
    if (!geoms) {
      return;
    }
    if (this.selectLayerSelect !== null) {
      this.map.removeInteraction(this.selectLayerSelect);
      this.selectLayerSelect = null;
    }
    this.selectLayerSource.addFeatures(geoms.map(e => new Feature(toPolygon(e))));
    this.selectLayerSelect = new Select(this.selectLayerOptions);
    this.selectLayerSelect.on('select', e => {
      this.internalSelect2(e);
      this.selected = null;
    });
    this.map.addInteraction(this.selectLayerSelect);
  }
  selectLayerStopSelect() {
    this.selectLayer.setVisible(false);
    this.selectLayerSource.clear();
    this.map.removeInteraction(this.selectLayerSelect);
    this.selectLayerSelect = null;
  }
  addPopoverToLayer(layerName) {
    if (layerName === '') {
      throw new Error(`No layer is defined`);
    }
    const selectedLayer = this.map.getAllLayers().find(e => e.get('name') === layerName);
    if (!selectedLayer) {
      throw new Error(`Layer not found`);
    }
    let condition = pointerMove;
    if (this.popupCondition === 'singleClick') {
      condition = singleClick;
    }
    this.pointerSelect = new Select({
      condition,
      layers: [selectedLayer]
    });
    this.popupFeature = new PopupFeature({
      select: this.pointerSelect,
      canFix: false,
      template: this.popupTemplate
    });
    this.map.addOverlay(this.popupFeature);
    this.map.addInteraction(this.pointerSelect);
  }
  removePopover() {
    if (this.pointerSelect !== null) {
      this.map.removeInteraction(this.pointerSelect);
      this.pointerSelect = null;
    }
    if (this.popupFeature !== null) {
      this.map.removeOverlay(this.popupFeature);
      this.popupFeature = null;
    }
  }

  // TODO: write a reset function what reset every interaction to null

  toggleGeolocation(state) {
    this.positionLayer.setVisible(state);
    this.geolocation.setTracking(state);
  }
}
const MapContextProvider = ({
  viewOptions,
  children
}) => {
  // map state
  const [map] = useState(() => new MapInternalClass(viewOptions));
  return /*#__PURE__*/React.createElement(MapContext.Provider, {
    value: map
  }, children);
};
const useMap = () => {
  return useContext(MapContext);
};
export { useMap, MapContextProvider };